import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, fmtDateTime } from "@/lib/utils-app";
import { ShieldCheck, ShieldX, Check, X, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  if (status === "approved") return <Badge className="bg-emerald-600 text-white hover:bg-emerald-600"><ShieldCheck className="mr-1 h-3 w-3" />Approved</Badge>;
  if (status === "rejected") return <Badge className="bg-rose-600 text-white hover:bg-rose-600"><ShieldX className="mr-1 h-3 w-3" />Rejected</Badge>;
  if (status === "consumed") return <Badge variant="secondary">Consumed</Badge>;
  if (status === "expired") return <Badge variant="secondary">Expired</Badge>;
  return <Badge variant="outline">Pending</Badge>;
}

const DURATION_OPTIONS = [
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "90", label: "1.5 hours (default)" },
  { value: "120", label: "2 hours" },
  { value: "180", label: "3 hours" },
  { value: "240", label: "4 hours" },
];

export default function PermissionsPage() {
  const [filter, setFilter] = useState("pending");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [durations, setDurations] = useState({}); // {permId: "90"}

  const load = async () => {
    const params = filter === "all" ? {} : { status: filter };
    const { data } = await api.get("/admin/attendance/permissions", { params });
    setList(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const decide = async (id, approve) => {
    setBusy(true);
    try {
      const payload = { approve, note: null };
      if (approve) {
        const mins = parseInt(durations[id] || "90", 10);
        if (mins > 0) payload.duration_minutes = mins;
      }
      await api.post(`/admin/attendance/permissions/${id}/decide`, payload);
      toast.success(approve ? `Approved (${durations[id] || "90"} min)` : "Rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const fmtExpiry = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short", timeZone: "Asia/Kolkata" });
    } catch { return null; }
  };

  return (
    <AppShell title="Permission requests" subtitle="Out-of-window capture permissions from supervisors">
      <div className="flex flex-col gap-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-3">
            <ClipboardCheck className="h-4 w-4 text-primary" />
            <span className="text-sm">Showing</span>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="perm-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="consumed">Consumed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {list.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Nothing here.</CardContent></Card>
        ) : (
          <div className="grid gap-2" data-testid="perm-list">
            {list.map((p) => (
              <Card key={p.id} data-testid={`perm-${p.id}`}>
                <CardContent className="flex flex-wrap items-start gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-medium">{p.supervisor_name}</div>
                      <Badge variant="secondary" className="text-[11px]">
                        {p.role === "pmc_supervisor" ? "PMC" : "Contractor"}
                      </Badge>
                      <StatusBadge status={p.status} />
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {p.park_name} · {fmtDate(p.date)}
                      {p.window_label ? <> · {p.window_label}</> : null}
                      {p.created_at ? <> · requested {fmtDateTime(p.created_at)}</> : null}
                    </div>
                    <div className="mt-1.5 text-sm">{p.reason}</div>
                    {p.status === "approved" && p.expires_at ? (
                      <div className="mt-1 text-xs text-emerald-700">
                        Valid until {fmtExpiry(p.expires_at)} IST{typeof p.used_count === "number" ? ` · ${p.used_count} capture${p.used_count === 1 ? "" : "s"} used` : ""}
                      </div>
                    ) : null}
                    {p.decision_note && (
                      <div className="mt-1 text-xs text-muted-foreground">Note: {p.decision_note}</div>
                    )}
                  </div>
                  {p.status === "pending" && (
                    <div className="flex flex-col items-end gap-2">
                      <Select
                        value={durations[p.id] || "90"}
                        onValueChange={(v) => setDurations((d) => ({ ...d, [p.id]: v }))}
                      >
                        <SelectTrigger className="h-8 w-[160px] text-xs" data-testid={`perm-${p.id}-duration`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DURATION_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => decide(p.id, false)}
                          disabled={busy}
                          data-testid={`perm-${p.id}-reject`}
                        >
                          <X className="mr-1 h-4 w-4" /> Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => decide(p.id, true)}
                          disabled={busy}
                          data-testid={`perm-${p.id}-approve`}
                        >
                          <Check className="mr-1 h-4 w-4" /> Approve
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
